import { apiFetchWithRetry } from './apiFetch';

/** Mirrors the API's ModerationStatus enum. */
export type ModerationStatus = 'PENDING' | 'APPROVED' | 'FLAGGED' | 'REVIEW_REQUESTED' | 'ADMIN_APPROVED' | 'ADMIN_REJECTED';

export type ModeratedTarget = 'ACHIEVEMENT_ASSET' | 'HYPEPAGE_PROFILE_IMAGE' | 'POST_ASSET' | 'HYPE_PROFILE_IMAGE';

/** Short label for where an image came from — shown on the queue card. */
export function targetLabel(target: ModeratedTarget): string {
  switch (target) {
    case 'ACHIEVEMENT_ASSET':
      return 'Achievement proof';
    case 'POST_ASSET':
      return 'Flair / post photo';
    case 'HYPEPAGE_PROFILE_IMAGE':
      return 'Profile photo';
    case 'HYPE_PROFILE_IMAGE':
      return 'Verifier photo';
    default:
      return target;
  }
}

/**
 * Can the student who owns this surface appeal it?
 *
 * The verifier's photo can't be: it belongs to an external party who isn't a
 * user, so there is no one to appeal on its behalf. Admins still decide it —
 * they just won't ever see a student note attached.
 */
export function isAppealableTarget(target: ModeratedTarget): boolean {
  return target !== 'HYPE_PROFILE_IMAGE';
}

export interface ModerationQueueItem {
  target: ModeratedTarget;
  id: string;
  imageUrl: string | null;
  status: ModerationStatus;
  /** Comma-separated provider categories that tripped, or null. */
  categories: string | null;
  studentReviewNote: string | null;
  studentName: string | null;
  studentEmail: string | null;
  /** Achievement title, or "Profile photo". */
  context: string | null;
  checkedAt: string | null;
}

/**
 * The one category we will not put on screen.
 *
 * Every user of this platform is a minor. An image the filter tagged
 * `sexual/minors` is suspected CSAM, and there is no version of "click to
 * reveal" that makes displaying it to a staff member acceptable — legally or
 * otherwise. The reviewer gets the metadata and can uphold the block without
 * ever loading the bytes.
 *
 * This is a display guard, not a safety control: the server already keeps
 * these off every public surface regardless of what this file does.
 */
export function isUnviewable(categories: string | null): boolean {
  return splitCategories(categories).includes('sexual/minors');
}

export function splitCategories(categories: string | null): string[] {
  return (categories ?? '')
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
}

/** Human label for a raw provider category key. */
export function categoryLabel(category: string): string {
  const LABELS: Record<string, string> = {
    'sexual/minors': 'Sexual — minors',
    sexual: 'Sexual',
    'violence/graphic': 'Graphic violence',
    violence: 'Violence',
    'self-harm/intent': 'Self-harm — intent',
    'self-harm/instructions': 'Self-harm — instructions',
    'self-harm': 'Self-harm',
    'harassment/threatening': 'Harassment — threatening',
    harassment: 'Harassment',
    'hate/threatening': 'Hate — threatening',
    hate: 'Hate',
    illicit: 'Illicit',
    'illicit/violent': 'Illicit — violent',
    unspecified: 'Unspecified',
  };
  return LABELS[category] ?? category;
}

/** Categories serious enough to colour red rather than amber. */
export function isSevereCategory(category: string): boolean {
  return category === 'sexual/minors' || category === 'sexual' || category.startsWith('self-harm') || category === 'violence/graphic';
}

export const moderationService = {
  /**
   * Images awaiting a human decision. Student appeals sort first; images the
   * model blocked that nobody appealed follow.
   */
  listQueue: async (): Promise<{ items: ModerationQueueItem[] }> => {
    const response = await apiFetchWithRetry('/v1/admin/moderation/queue');
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to load the review queue');
    }
    return data as { items: ModerationQueueItem[] };
  },

  /** Overturn the filter — the image becomes publicly visible. */
  approve: async (target: ModeratedTarget, id: string, note?: string): Promise<{ status: ModerationStatus }> => {
    return await decide('approve', target, id, note);
  },

  /** Uphold the filter — the image stays hidden, and the student can't re-appeal. */
  reject: async (target: ModeratedTarget, id: string, note?: string): Promise<{ status: ModerationStatus }> => {
    return await decide('reject', target, id, note);
  },
};

async function decide(action: 'approve' | 'reject', target: ModeratedTarget, id: string, note?: string): Promise<{ status: ModerationStatus }> {
  const response = await apiFetchWithRetry(`/v1/admin/moderation/${target}/${id}/${action}`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.message || `Couldn't ${action} this image`);
  }
  return data as { status: ModerationStatus };
}
